import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Search, ArrowRight, Clock, Shield, Globe, CheckCircle, 
  Users, Star, ChevronRight, Plane, FileCheck, CreditCard,
  Zap, FileText, BadgeCheck, ArrowUpRight, Sparkles, Timer,
  Building2, DollarSign, Percent, X
} from 'lucide-react';
import { countriesApi, visaTypesApi, eligibilityApi, applicationsApi } from '../lib/api';
import CountryFlag from '../components/CountryFlag';
import { useAuth } from '../contexts/AuthContext';

const LandingPage = () => {
  const [countries, setCountries] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [popularVisas, setPopularVisas] = useState([]);
  const [passportCountry, setPassportCountry] = useState('India');
  const [destinationCountry, setDestinationCountry] = useState('');
  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [activeTab, setActiveTab] = useState('trending');
  const [searchQuery, setSearchQuery] = useState('');
  const [draftToResume, setDraftToResume] = useState(null);
  const [draftDismissed, setDraftDismissed] = useState(() => sessionStorage.getItem('home_draft_dismissed') === '1');
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!user || user.role !== 'applicant') { setDraftToResume(null); return; }
    const fetchDraft = async () => {
      try {
        const res = await applicationsApi.getMyApplications();
        const drafts = (res.data || []).filter((a) => a.status === 'draft');
        if (drafts.length === 0) return;
        // Most recently updated draft
        drafts.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
        setDraftToResume(drafts[0]);
      } catch (err) {
        console.error('Failed to fetch drafts:', err);
      }
    };
    fetchDraft();
  }, [user]);

  const dismissDraftBanner = () => {
    sessionStorage.setItem('home_draft_dismissed', '1');
    setDraftDismissed(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [countriesRes, allCountriesRes, visasRes] = await Promise.all([
          countriesApi.getPopular(),
          countriesApi.getAll(),
          visaTypesApi.getPopular()
        ]);
        setCountries(countriesRes.data);
        setAllCountries(allCountriesRes.data);
        setPopularVisas(visasRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleEligibilityCheck = async (e) => {
    e.preventDefault();
    if (!destinationCountry) return;
    setCheckingEligibility(true);
    try {
      const response = await eligibilityApi.check(passportCountry, destinationCountry);
      setEligibilityResult(response.data);
    } catch (error) {
      console.error('Eligibility check error:', error);
    } finally {
      setCheckingEligibility(false);
    }
  };

  const filteredCountries = searchQuery
    ? allCountries.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : (activeTab === 'trending' ? countries : allCountries);

  const getDeliveryDate = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getRandomProcessed = (name) => {
    const hash = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    return ((hash % 80) + 20) + 'K+';
  };

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <Navbar transparent />

      {/* Continue Draft Banner */}
      {draftToResume && !draftDismissed && (
        <div
          className="fixed top-20 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-3xl"
          data-testid="continue-draft-banner"
        >
          <div className="flex items-center gap-3 bg-white border border-[#FF5A36]/30 shadow-lg rounded-2xl pl-4 pr-2 py-2.5 animate-fade-in">
            <div className="w-10 h-10 rounded-xl bg-[#FF5A36]/10 flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-[#FF5A36]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0A1E40] truncate">
                You have an unfinished application
                {draftToResume.country?.name && (
                  <span className="font-normal text-[#4A5568]"> for </span>
                )}
                {draftToResume.country?.name && (
                  <span className="inline-flex items-center gap-1.5">
                    <CountryFlag code={draftToResume.country.code} size="sm" />
                    {draftToResume.country.name}
                  </span>
                )}
              </p>
              <p className="text-[11px] text-[#4A5568]">
                Pick up where you left off — your progress is auto-saved.
              </p>
            </div>
            <Button
              onClick={() => navigate(`/apply?edit=${draftToResume.application_id}`)}
              className="bg-[#FF5A36] hover:bg-[#E0492B] text-white text-sm font-semibold shrink-0"
              data-testid="resume-draft-btn"
            >
              Resume <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <button
              type="button"
              onClick={dismissDraftBanner}
              className="text-[#9CA3AF] hover:text-[#0A1E40] p-1.5 rounded-md shrink-0"
              aria-label="Dismiss"
              data-testid="dismiss-draft-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Hero Section - Atlys-inspired search-first UX */}
      <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-24" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/37214d83-8b7f-4221-9c1d-bd7f6414452d/images/c320b650af3272de0f4350da7974d4d61ccfcb4f231e6d429191d5378d9e9f26.png')`
          }}
        />
        <div className="hero-overlay absolute inset-0" />
        
        <SEO
          title="Apply for Visa Online"
          description="Fast, secure, and hassle-free visa processing. Apply for tourist, business, work, and student visas to 150+ countries with AI-powered document verification."
          keywords="visa application, apply visa online, tourist visa, business visa, student visa, e-visa, visa processing, EasyMyVisa"
          canonical="/"
          jsonLd={{
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "name": "EasyMyVisa",
                "url": window.location.origin,
                "description": "Fast, secure, and hassle-free visa processing at your fingertips.",
                "contactPoint": { "@type": "ContactPoint", "contactType": "customer support", "availableLanguage": ["English", "Hindi"] }
              },
              {
                "@type": "WebSite",
                "name": "EasyMyVisa",
                "url": window.location.origin,
                "potentialAction": { "@type": "SearchAction", "target": `${window.location.origin}/countries?q={search_term_string}`, "query-input": "required name=search_term_string" }
              }
            ]
          }}
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm mb-6 border border-white/20">
              <Zap className="w-4 h-4 text-[#FF5A36]" />
              <span>Visas On Time, Guaranteed</span>
            </div>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Get Your Visa <br />
              <span className="text-[#FF5A36]">Fast & Hassle-Free</span>
            </h1>
            <p className="text-white/80 text-lg mb-8 max-w-xl">
              Apply online for 150+ countries. AI-powered processing, real-time tracking, and guaranteed on-time delivery.
            </p>
            
            {/* Search Bar - Atlys inspired */}
            <div className="bg-white rounded-2xl p-2 shadow-2xl max-w-2xl flex items-center gap-2" data-testid="search-bar">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <input
                  type="text"
                  placeholder="Where do you want to go? (e.g., USA, UK, Dubai)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-xl bg-transparent text-[#0A1E40] placeholder:text-[#9CA3AF] focus:outline-none text-base"
                  data-testid="search-input"
                />
              </div>
              {/* <Button 
                className="bg-[#FF5A36] hover:bg-[#E0492B] text-white rounded-xl px-8 py-4 h-auto font-semibold shrink-0"
                onClick={() => searchQuery && navigate('/countries')}
                data-testid="search-btn"
              >
                Search <ArrowRight className="w-5 h-5 ml-2" />
              </Button> */}

              <Button 
                  className="bg-[#FF5A36] hover:bg-[#E0492B] text-white rounded-xl px-8 py-4 h-auto font-semibold shrink-0"
                  onClick={() => {
                    if (searchQuery) {
                      sessionStorage.setItem('destination_search', searchQuery);
                      navigate('/countries');
                    }
                  }}
                  data-testid="search-btn"
                >
                  Search <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
            </div>

            {/* Quick Tags */}
            <div className="flex flex-wrap gap-2 mt-4">
              {['UAE', 'Singapore', 'USA', 'UK', 'Thailand', 'Canada'].map(tag => (
                <button
                  key={tag}
                  className="px-4 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm border border-white/20 backdrop-blur-sm transition-all"
                  onClick={() => setSearchQuery(tag)}
                  data-testid={`quick-tag-${tag.toLowerCase()}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Stats Bar */}
      <section className="py-6 bg-white border-y border-[#D1CBBF]" data-testid="trust-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between overflow-x-auto gap-8">
            {[
              { number: '50,000+', label: 'Visas Processed', icon: BadgeCheck },
              { number: '150+', label: 'Countries', icon: Globe },
              { number: '98%', label: 'On-Time Delivery', icon: Timer },
              { number: '4.8/5', label: 'Customer Rating', icon: Star },
              { number: '24/7', label: 'Expert Support', icon: Shield }
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-3 shrink-0">
                <div className="w-10 h-10 rounded-xl bg-[#FF5A36]/10 flex items-center justify-center">
                  <stat.icon className="w-5 h-5 text-[#FF5A36]" />
                </div>
                <div>
                  <p className="text-lg font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {stat.number}
                  </p>
                  <p className="text-xs text-[#4A5568]">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trending Countries - Visa2fly Style Cards */}
      <section className="py-16" data-testid="trending-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Tabs */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {searchQuery ? `Results for "${searchQuery}"` : 'Trending Countries'}
              </h2>
              <p className="text-[#4A5568] mt-1">
                {searchQuery ? `${filteredCountries.length} destinations found` : 'Most popular visa destinations'}
              </p>
            </div>
            {!searchQuery && (
              <div className="hidden md:flex items-center gap-1 bg-[#F5F0E6] border border-[#D1CBBF] rounded-xl p-1">
                {[
                  { key: 'trending', label: 'Trending' },
                  { key: 'all', label: 'All Countries' }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      activeTab === tab.key
                        ? 'bg-[#0A1E40] text-white shadow-sm'
                        : 'text-[#4A5568] hover:text-[#0A1E40]'
                    }`}
                    data-testid={`tab-${tab.key}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Country Cards - Atlys + Visa2fly hybrid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 stagger-children">
            {filteredCountries.slice(0, 8).map((country) => (
              <Link
                key={country.country_id}
                to={`/countries/${country.code?.toLowerCase()}`}
                className="group bg-white rounded-2xl overflow-hidden border border-[#D1CBBF] card-hover"
                data-testid={`country-card-${country.code}`}
              >
                {/* Card Image */}
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={country.image_url}
                    alt={country.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

                  {country.is_popular && (
                    <div className="absolute top-3 right-3 bg-[#FF5A36] text-white px-3 py-1 rounded-full text-xs font-bold">
                      Popular
                    </div>
                  )}

                  {/* Country Name on Image */}
                  <div className="absolute bottom-3 left-3 flex items-center gap-2">
                    <CountryFlag code={country.code} size="lg" />
                    <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {country.name}
                    </h3>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-4">
                  {/* Quick Info */}
                  <div className="flex items-center gap-4 text-xs text-[#4A5568] mb-3">
                    <span className="flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5" /> e-Visa
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> 3-5 days
                    </span>
                  </div>

                  {/* Documents */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {(country.requirements || []).slice(0, 3).map((req, i) => (
                      <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#F5F0E6] text-xs text-[#4A5568]">
                        {req.length > 15 ? req.substring(0, 15) + '...' : req}
                      </span>
                    ))}
                  </div>

                  {/* Arrow */}
                  <div className="flex items-center justify-end pt-3 border-t border-[#F5F0E6]">
                    <div className="w-8 h-8 rounded-full bg-[#FF5A36] flex items-center justify-center group-hover:scale-110 transition-transform">
                      <ArrowUpRight className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/countries">
              <Button variant="outline" className="btn-secondary" data-testid="view-all-btn">
                View All Countries <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Express Processing Banner - Visa2fly Bolt Inspired */}
      <section className="py-16 bg-[#0A1E40] relative overflow-hidden" data-testid="express-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#FF5A36] rounded-full blur-[128px]" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-blue-500 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#FF5A36] text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
              <Zap className="w-4 h-4" />
              INTRODUCING EXPRESS VISA
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Our Fastest Visa Service
            </h2>
            <p className="text-white/60 mt-3 max-w-lg mx-auto">
              Priority processing for urgent travel needs. Get your visa before everyone else.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: 'Dedicated Fast-Lane', desc: 'Your application moves through a priority queue for instant processing.' },
              { icon: Sparkles, title: 'Instant Case Start', desc: 'Processing begins the moment you submit. No waiting in line.' },
              { icon: Shield, title: 'Early Error Detection', desc: 'AI catches issues instantly, preventing delays in your application.' }
            ].map((feature, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm hover:bg-white/10 transition-all">
                <div className="w-12 h-12 rounded-xl bg-[#FF5A36] flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-white/60 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Visa Packages - Enhanced */}
      <section className="py-16" data-testid="visa-packages-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Popular Visa Packages
              </h2>
              <p className="text-[#4A5568] mt-1">Best-selling visa services with guaranteed processing</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {popularVisas.slice(0, 6).map((visa) => (
              <div 
                key={visa.visa_type_id} 
                className="bg-white rounded-2xl border border-[#D1CBBF] overflow-hidden card-hover"
                data-testid={`visa-package-${visa.visa_type_id}`}
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <CountryFlag code={visa.country?.code} size="xl" />
                      <div>
                        <h3 className="font-semibold text-[#0A1E40] text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {visa.country?.name}
                        </h3>
                        <p className="text-sm text-[#4A5568]">{visa.name}</p>
                      </div>
                    </div>
                    {visa.is_popular && (
                      <span className="bg-[#FF5A36]/10 text-[#FF5A36] text-xs font-bold px-2.5 py-1 rounded-full">
                        Popular
                      </span>
                    )}
                  </div>
                  
                  {/* Visa Details Grid */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-[#F5F0E6] rounded-xl p-3 text-center">
                      <p className="text-[10px] text-[#4A5568] uppercase tracking-wider mb-0.5">Type</p>
                      <p className="text-sm font-semibold text-[#0A1E40]">e-Visa</p>
                    </div>
                    <div className="bg-[#F5F0E6] rounded-xl p-3 text-center">
                      <p className="text-[10px] text-[#4A5568] uppercase tracking-wider mb-0.5">Valid</p>
                      <p className="text-sm font-semibold text-[#0A1E40]">{visa.validity}</p>
                    </div>
                    <div className="bg-[#F5F0E6] rounded-xl p-3 text-center">
                      <p className="text-[10px] text-[#4A5568] uppercase tracking-wider mb-0.5">Stay</p>
                      <p className="text-sm font-semibold text-[#0A1E40]">{visa.max_stay}</p>
                    </div>
                  </div>

                  {/* Documents */}
                  <div className="mb-4">
                    <p className="text-xs text-[#4A5568] mb-2">Documents Needed:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(visa.documents_required || ['Passport', 'Photo']).slice(0, 4).map((doc, i) => (
                        <span key={i} className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-md">
                          {doc}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Processing Info */}
                  <div className="flex items-center gap-2 text-sm text-[#4A5568] mb-4">
                    <Clock className="w-4 h-4 text-[#FF5A36]" />
                    <span>Processing: {visa.processing_time}</span>
                  </div>
                </div>

                {/* Footer */}
                <div className="border-t border-[#F5F0E6] px-5 py-4 bg-[#FAFAF5] flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[#4A5568]">Starting from</p>
                    <span className="text-2xl font-bold text-[#0A1E40]">
                      {visa.fee?.toLocaleString('en-IN', { style: 'currency', currency: visa.currency || 'INR', maximumFractionDigits: 0 })}
                    </span>
                    <span className="text-xs text-[#4A5568]"> /person</span>
                  </div>
                  <Link to={`/apply?visa=${visa.visa_type_id}&country=${visa.country_id}`}>
                    <Button className="bg-[#FF5A36] hover:bg-[#E0492B] text-white rounded-xl px-5">
                      Apply <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility Checker */}
      <section className="py-16 bg-white" data-testid="eligibility-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#F5F0E6] rounded-3xl p-8 md:p-12 border border-[#D1CBBF]">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Check Your Visa Eligibility
              </h2>
              <p className="text-[#4A5568] mt-2">Instantly know if you need a visa and what type</p>
            </div>
            <form onSubmit={handleEligibilityCheck} className="max-w-xl mx-auto space-y-4" data-testid="eligibility-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#4A5568] mb-1 block font-medium">Your Passport</label>
                  <Input
                    value={passportCountry}
                    onChange={(e) => setPassportCountry(e.target.value)}
                    placeholder="e.g., India"
                    data-testid="passport-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#4A5568] mb-1 block font-medium">Destination</label>
                  <Input
                    value={destinationCountry}
                    onChange={(e) => setDestinationCountry(e.target.value)}
                    placeholder="e.g., USA, UK"
                    data-testid="destination-input"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#FF5A36] hover:bg-[#E0492B] text-white rounded-xl py-6 font-semibold"
                disabled={checkingEligibility || !destinationCountry}
                data-testid="check-eligibility-btn"
              >
                {checkingEligibility ? 'Checking...' : 'Check Eligibility'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>
            
            {eligibilityResult && (
              <div className="mt-6 p-5 bg-white rounded-xl border border-[#D1CBBF] max-w-xl mx-auto" data-testid="eligibility-result">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-[#0A1E40] text-lg">{eligibilityResult.country?.name}</p>
                    <p className="text-[#4A5568] mt-1">{eligibilityResult.message}</p>
                    {eligibilityResult.visa_types?.length > 0 && (
                      <Button 
                        variant="link" 
                        className="p-0 h-auto text-[#FF5A36] mt-2 font-semibold"
                        onClick={() => navigate(`/countries/${eligibilityResult.country.code?.toLowerCase()}`)}
                      >
                        View {eligibilityResult.visa_types.length} visa options <ChevronRight className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* How It Works - Enhanced */}
      <section className="py-16" id="how-it-works" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#FF5A36] text-sm font-bold tracking-[0.2em] uppercase">Simple Process</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A1E40] mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How EasyMyVisa Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: 1, title: 'Select Destination', desc: 'Choose your country and visa type from 150+ destinations.', icon: Globe, color: 'bg-blue-500' },
              { step: 2, title: 'Fill Application', desc: 'AI-powered form with passport scanning. Quick and error-free.', icon: FileCheck, color: 'bg-[#FF5A36]' },
              { step: 3, title: 'Make Payment', desc: 'Secure payment via RazorPay. Transparent pricing, no hidden fees.', icon: CreditCard, color: 'bg-emerald-500' },
              { step: 4, title: 'Get Your Visa', desc: 'Track in real-time. Receive your visa digitally before travel.', icon: Plane, color: 'bg-purple-500' }
            ].map((item) => (
              <div key={item.step} className="relative">
                <div className="bg-white rounded-2xl p-6 border border-[#D1CBBF] h-full">
                  <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center mb-4`}>
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-xs font-bold text-[#FF5A36] mb-2 uppercase tracking-wider">Step {item.step}</div>
                  <h3 className="text-lg font-semibold text-[#0A1E40] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#4A5568]">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Feature Section */}
      <section className="py-16 bg-[#0A1E40]" data-testid="ai-feature-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FF5A36]/20 text-[#FF5A36] px-3 py-1.5 rounded-full text-sm font-bold mb-4">
                <Sparkles className="w-4 h-4" /> AI-POWERED
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Smart Passport Scanning
              </h2>
              <p className="text-white/70 mb-6">
                Our advanced AI technology extracts your passport details automatically, reducing errors and saving you time.
              </p>
              <ul className="space-y-3">
                {['Instant data extraction', 'Document verification', 'Error detection', '100% secure processing'].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-white">
                    <div className="w-6 h-6 rounded-full bg-[#FF5A36] flex items-center justify-center shrink-0">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    {feature}
                  </li>
                ))}
              </ul>
              <Link to="/register" className="inline-block mt-8">
                <Button className="bg-[#FF5A36] hover:bg-[#E0492B] text-white rounded-xl px-8 py-3 font-semibold">
                  Try It Now <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/37214d83-8b7f-4221-9c1d-bd7f6414452d/images/5b65a874568f9310b8948f181a484f7efca0ad05806b18043e2aa0a7faba2b36.png"
                alt="AI Passport Scanning"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Partner / Join With Us Block */}
      <section className="py-16 px-4" data-testid="partner-block">
        <div className="max-w-6xl mx-auto">
          <div className="bg-[#0A1E40] rounded-3xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 bg-[#FF5A36]/20 text-[#FF5A36] px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider w-fit mb-5">
                  <Building2 className="w-3.5 h-3.5" /> Partner Program
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Are You a Travel Agent or Corporate?
                </h2>
                <p className="text-white/70 mb-6 leading-relaxed">
                  Join our B2B partner network and grow your business. Get special discounts, earn commissions, and manage all client visa applications from one dashboard.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {[
                    { icon: DollarSign, text: 'Earn Commissions' },
                    { icon: Percent, text: 'Special Discounts' },
                    { icon: Users, text: 'Bulk Submissions' },
                    { icon: Shield, text: 'Priority Processing' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-white/80 text-sm">
                      <item.icon className="w-4 h-4 text-[#FF5A36] shrink-0" />
                      {item.text}
                    </div>
                  ))}
                </div>
                <Link to="/partner" className="w-fit">
                  <Button className="bg-[#FF5A36] hover:bg-[#E0492B] text-white px-8 py-5 rounded-xl text-base font-semibold" data-testid="landing-partner-btn">
                    Join as Partner <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="hidden lg:flex items-center justify-center p-8 bg-gradient-to-br from-[#FF5A36]/10 to-transparent">
                <div className="space-y-4 w-full max-w-sm">
                  {[
                    { val: '500+', label: 'Active Partners' },
                    { val: 'Up to 15%', label: 'Partner Discount' },
                    { val: 'Up to 10%', label: 'Commission Rate' },
                  ].map((s, i) => (
                    <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                      <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.val}</p>
                      <p className="text-white/50 text-sm">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0A1E40] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Start Your Journey?
          </h2>
          <p className="text-[#4A5568] text-lg mb-8">
            Join thousands of satisfied travelers who've simplified their visa process.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="btn-primary text-lg px-10 py-6" data-testid="cta-get-started-btn">
                Get Started Free <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="btn-secondary text-lg px-10 py-6" data-testid="cta-contact-btn">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
